import axios, { AxiosError, type AxiosInstance } from "axios";
import type { ApiError, PageRequest, PageResponse } from "@/types/api";
import type {
  Campaign,
  CampaignContactScanRequest,
  CampaignContactImportResponse,
  CampaignFilter,
  CampaignImportRowError,
  CampaignActivationPreview,
  CampaignActivationContact,
  CampaignRun,
  CampaignWriteBody,
} from "@/types/campaign";
import { apiClient, clean } from "./apiClient";
import { handleSessionExpired } from "@/lib/authSession";
import { useProfileStore } from "@/store/profileStore";
import { useSettingsStore } from "@/store/settingsStore";
import { showActionError } from "@/lib/feedback";
import { sanitizeApiErrorMessage } from "@/utils/apiErrors";

/**
 * Campaigns are their own Cloud Run service, not a path on atlas-api — so the
 * base cannot be derived from `VITE_API_BASE_URL`.
 *
 * The default is the same-origin `/campaign-api` prefix, reverse-proxied to the
 * campaign service's own `/api/v1` base by vite in local dev and by nginx in
 * deployments (see `vite.config.ts` and `nginx.conf.template`).
 *
 * The prefix deliberately sits outside `/api`: the deployed load balancers
 * prefix-match `/api` and hand it to atlas-api before nginx is reached, so a
 * `/api/v1` prefix — however specific — came back as atlas-api's 404 rather
 * than reaching the campaign service.
 *
 * Going through the proxy keeps every call same-origin, so the campaign service
 * never has to CORS-allow the Atlas origins. Set `VITE_CAMPAIGNS_API_BASE_URL`
 * to an absolute URL to call the service directly instead — which then does
 * require CORS on its side.
 */
function resolveCampaignsBaseUrl(): string {
  const explicit = import.meta.env.VITE_CAMPAIGNS_API_BASE_URL as string | undefined;
  return (explicit?.trim() || "/campaign-api").replace(/\/$/, "");
}

/**
 * Acting user for `X-User-Id`, which the campaign service requires on writes
 * and stamps into created_by/modified_by.
 *
 * It wants a *numeric* id. Atlas identifies users by UUID, so there is nothing
 * to map from today: the profile's `userId` is only usable on the chance it is
 * already numeric, and otherwise this falls back to `VITE_CAMPAIGNS_USER_ID`
 * (default `1`) so the header is never simply missing. That fallback attributes
 * every campaign to the same user — fine for dev, wrong for production, and it
 * needs a real Atlas-user → campaign-user mapping before it ships.
 */
const CAMPAIGNS_FALLBACK_USER_ID =
  (import.meta.env.VITE_CAMPAIGNS_USER_ID as string | undefined)?.trim() || "1";

function resolveActingUserId(): string {
  const profileUserId = useProfileStore.getState().profileData?.userId?.trim();
  if (profileUserId && /^\d+$/.test(profileUserId)) return profileUserId;
  return CAMPAIGNS_FALLBACK_USER_ID;
}

const campaignsClient: AxiosInstance = axios.create({
  baseURL: resolveCampaignsBaseUrl(),
  timeout: 30_000,
  withCredentials: import.meta.env.VITE_API_WITH_CREDENTIALS === "true",
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

campaignsClient.interceptors.request.use((config) => {
  const keycloak = (window as unknown as { keycloak?: { token?: string } }).keycloak;
  if (keycloak?.token) {
    config.headers.Authorization = `Bearer ${keycloak.token}`;
  }

  // The campaign service reads the acting user from X-User-Id rather than the
  // bearer token, and 400s on writes without it — so it always travels.
  config.headers["X-User-Id"] = resolveActingUserId();

  if (import.meta.env.VITE_USE_MOCK_AUTH === "true" && keycloak?.token === "mock-token") {
    config.headers["X-Mock-User-Email"] = import.meta.env.VITE_MOCK_USER_EMAIL || "test@bridgeai.co.uk";
  }
  return config;
});

/**
 * An error from the campaigns API, with the CSV import's row-wise failures kept
 * as data rather than flattened into `message` — the drawer lists them in a
 * modal, which needs the row number and field per entry.
 */
export interface CampaignApiError extends ApiError {
  rowErrors?: CampaignImportRowError[];
}

function isRowError(value: unknown): value is CampaignImportRowError {
  return (
    !!value &&
    typeof value === "object" &&
    "row_number" in value &&
    "message" in value
  );
}

/** Pull the row-wise CSV import failures out of a rejected campaigns call. */
export function getCampaignImportRowErrors(err: unknown): CampaignImportRowError[] | null {
  if (!err || typeof err !== "object") return null;
  const rows = (err as CampaignApiError).rowErrors;
  return rows?.length ? rows : null;
}

campaignsClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) => {
    const data = error.response?.data as
      | (ApiError & {
          detail?: string;
          errors?: Record<string, string> | CampaignImportRowError[];
        })
      | undefined;

    // `/campaigns/import` reports per-row failures as an array of objects. They
    // are far too many to read in a toast, so they travel structured and the
    // message falls back to the `detail` summary ("Import failed with 5 …").
    const rowErrors =
      Array.isArray(data?.errors) && data.errors.every(isRowError) ? data.errors : undefined;

    // RFC-7807 validation failures carry the useful part in `errors` — the
    // `detail` on its own is just "Validation failed", which tells nobody
    // which field the service rejected.
    const fieldErrors =
      data?.errors && !Array.isArray(data.errors)
        ? Object.entries(data.errors)
            .map(([field, problem]) => `${field}: ${problem}`)
            .join("; ")
        : "";
    let message = sanitizeApiErrorMessage(
      fieldErrors || data?.message || data?.detail || error.message,
    );

    if (error.response?.status === 401) {
      handleSessionExpired();
      message = "Your session has expired. Please sign in again.";
    } else if (error.response?.status === 403) {
      message = "You are not allowed to perform this action or do not have the necessary role.";
      showActionError(message, { title: "Access denied" });
    }

    const normalized: CampaignApiError = {
      status: error.response?.status ?? 0,
      code: data?.code ?? "NETWORK_ERROR",
      message,
      path: data?.path ?? error.config?.url ?? "",
      timestamp: data?.timestamp ?? new Date().toISOString(),
      ...(rowErrors?.length ? { rowErrors } : {}),
    };
    return Promise.reject(normalized);
  },
);

/** Wire envelope of the campaigns list endpoint. */
interface CampaignPage {
  items: Campaign[];
  page: number;
  size: number;
  total_elements: number;
  total_pages: number;
}

/** Adapts the snake_case page envelope to the shape `usePagedResource` expects. */
function toPageResponse(raw: CampaignPage): PageResponse<Campaign> {
  const page = raw.page ?? 0;
  const totalPages = raw.total_pages ?? 0;
  return {
    content: raw.items ?? [],
    page,
    size: raw.size ?? 20,
    totalElements: raw.total_elements ?? 0,
    totalPages,
    hasNext: page + 1 < totalPages,
    hasPrevious: page > 0,
  };
}

/** Wire envelope of the campaign-runs endpoint — same shape as {@link CampaignPage}. */
interface CampaignRunPage {
  items: CampaignRun[];
  page: number;
  size: number;
  total_elements: number;
  total_pages: number;
}

/** Largest page the runs endpoint serves — it silently caps anything above. */
const RUNS_PAGE_SIZE = 100;
/** Ceiling on the paging walk, so a service that ignores `page` can't spin. */
const RUNS_MAX_PAGES = 5;

export const campaignsService = {
  list(filter: CampaignFilter, page: PageRequest): Promise<PageResponse<Campaign>> {
    // The campaign service pages but does not sort — drop the sort/direction
    // `usePagedResource` carries so they aren't sent as dead query params.
    const paging = { page: page.page, size: page.size };
    return campaignsClient
      .get<CampaignPage>("/campaigns", { params: { ...clean(filter), ...clean(paging) } })
      .then((r) => toPageResponse(r.data));
  },

  getOne: (id: number | string) =>
    campaignsClient.get<Campaign>(`/campaigns/${id}`).then((r) => r.data),

  create: (body: CampaignWriteBody) =>
    campaignsClient.post<Campaign>("/campaigns", body).then((r) => r.data),

  /**
   * Create a campaign whose recipients come from an uploaded CSV. Same body as
   * {@link create} bar `labels` → `csv`, but a different endpoint: the service
   * has to import the contacts before the campaign can target them.
   *
   * The imported contacts land in Atlas, so a legal entity always travels in
   * the body as `legal_entity_id` — the service 400s when it is blank. Callers
   * that let the user pick a scope pass `legalEntityId`; everyone else falls
   * back to the account-level selection.
   */
  createFromCsv: (body: CampaignWriteBody, legalEntityId?: string | null) =>
    campaignsClient
      .post<Campaign>("/campaigns/import", {
        ...body,
        legal_entity_id: legalEntityId ?? useSettingsStore.getState().legalEntityId,
      })
      .then((r) => r.data),

  /**
   * Dry-run the CSV's contacts against Atlas before importing them.
   *
   * This one lives on the Atlas UI API, not the campaign service — the contacts
   * it compares against are Atlas records — so it goes out on `apiClient` and
   * the response is left raw for `normalizeScanResults` to read.
   */
  scanContacts: (body: CampaignContactScanRequest): Promise<unknown> =>
    apiClient.post("/campaigns/contacts/scan", body).then((r) => r.data),

  /**
   * Writes the CSV rows into Atlas: create missing labels, upsert contacts,
   * assign contacts. Same body as {@link scanContacts}. Returns PASS/FAIL in
   * the JSON (HTTP 200 either way).
   */
  importContacts: (body: CampaignContactScanRequest): Promise<CampaignContactImportResponse> =>
    apiClient.post("/campaigns/contacts/import", body).then((r) => r.data),

  /** Partial update — send only the fields the user actually changed. */
  update: (id: number | string, body: Partial<CampaignWriteBody>) =>
    campaignsClient.patch<Campaign>(`/campaigns/${id}`, body).then((r) => r.data),

  /**
   * Activate or deactivate a campaign — `PUT /campaigns/{id}/status`
   * `{ "status": "ACTIVE" | "INACTIVE" }`. Activate re-validates contacts.
   */
  updateStatus: (id: number | string, status: "ACTIVE" | "INACTIVE") =>
    campaignsClient.put<Campaign>(`/campaigns/${id}/status`, { status }).then((r) => r.data),

  /** Dry-run labelled contacts (including Alto sync) without changing status. */
  activationPreview: (id: number | string) =>
    campaignsClient
      .post<CampaignActivationPreview>(`/campaigns/${id}/activation-preview`)
      .then((r) => normalizeActivationPreview(r.data)),

  /** Goes live after contacts are ready. Immediate campaigns start sending now. */
  activate: (id: number | string) =>
    campaignsClient.post<Campaign>(`/campaigns/${id}/activate`).then((r) => r.data),

  remove: (id: number | string) => campaignsClient.delete(`/campaigns/${id}`),

  /**
   * One page of a campaign's runs, newest first.
   *
   * Note the path takes no id — `/campaigns/runs/{id}` addresses a *run*, and
   * the campaign is the `campaign_id` query param.
   */
  listRuns: (campaignId: number | string, page = 0, size = RUNS_PAGE_SIZE) =>
    campaignsClient
      .get<CampaignRunPage>("/campaigns/runs", { params: { campaign_id: campaignId, page, size } })
      .then((r) => r.data),

  /**
   * A campaign's whole run history, paged through, plus the total the service
   * reports. The drawer counts the failures itself, which is only honest over
   * the whole list — but the walk is capped, so `total` is carried separately
   * for the rare campaign with more runs than {@link RUNS_MAX_PAGES} holds.
   */
  listAllRuns: async (
    campaignId: number | string,
  ): Promise<{ runs: CampaignRun[]; total: number }> => {
    const runs: CampaignRun[] = [];
    let total = 0;
    for (let page = 0; page < RUNS_MAX_PAGES; page += 1) {
      const batch = await campaignsService.listRuns(campaignId, page, RUNS_PAGE_SIZE);
      const items = batch.items ?? [];
      runs.push(...items);
      total = batch.total_elements ?? runs.length;
      if (items.length < RUNS_PAGE_SIZE) break;
    }
    return { runs, total: Math.max(total, runs.length) };
  },

};

function asText(value: unknown): string | null {
  if (value == null) return null;
  const text = String(value).trim();
  return text || null;
}

function normalizeActivationPreview(raw: CampaignActivationPreview | Record<string, unknown>): CampaignActivationPreview {
  const source = (raw ?? {}) as Record<string, unknown>;
  const contactsRaw = (source.contacts ?? []) as Record<string, unknown>[];
  const contacts: CampaignActivationContact[] = Array.isArray(contactsRaw)
    ? contactsRaw.flatMap((row) => {
        const contact_id = asText(row.contact_id) ?? asText(row.contactId);
        if (!contact_id) return [];
        const issuesRaw = Array.isArray(row.issues) ? row.issues : [];
        return [
          {
            contact_id,
            name: asText(row.name),
            legal_entity_id: asText(row.legal_entity_id) ?? asText(row.legalEntityId),
            crm_sync_status: asText(row.crm_sync_status) ?? asText(row.crmSyncStatus),
            linked_to_crm: Boolean(row.linked_to_crm ?? row.linkedToCrm),
            issues: issuesRaw.flatMap((issue) => {
              if (!issue || typeof issue !== "object") return [];
              const rec = issue as Record<string, unknown>;
              const code = asText(rec.code);
              if (!code) return [];
              return [{ code, message: asText(rec.message) }];
            }),
          },
        ];
      })
    : [];
  const blocked =
    typeof source.blocked === "number"
      ? source.blocked
      : contacts.filter((c) => c.issues.length > 0).length;
  const total = typeof source.total === "number" ? source.total : contacts.length;
  const ready = typeof source.ready === "number" ? source.ready : Math.max(total - blocked, 0);
  const issueCountsRaw = (source.issue_counts ?? source.issueCounts ?? {}) as Record<string, number>;
  return {
    all_ready: Boolean(source.all_ready ?? source.allReady),
    total,
    ready,
    blocked,
    schedule_in_past: Boolean(source.schedule_in_past ?? source.scheduleInPast),
    send_mode: asText(source.send_mode) ?? asText(source.sendMode),
    code: asText(source.code),
    issue_counts: issueCountsRaw,
    contacts,
  };
}

// Labels and templates are Atlas resources, not campaign-service ones — see
// `labelsService` and `whatsappTemplateService`.
